import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFeatureFlag } from '../../../contexts/featureFlagContext';
import { useLayoutStore } from '../../../lib/stores/LayoutStore';
import { OfficeWorkspaceAssistHostContext } from '../../OfficeWorkspace/OfficeWorkspaceAssistContext';
import { useProposalRecordShellEnabled } from '../../RecordShell/recordShellFlag';
const ProposalV2ReactContext = /*#__PURE__*/ createContext(null);
export function ProposalV2Provider({ children, storageKey }) {
    const [restored] = useState(()=>{
        if (storageKey) {
            try {
                const saved = JSON.parse(sessionStorage.getItem(storageKey) ?? 'null');
                if (typeof saved?.id === 'string' && /^[0-9a-f-]{36}$/i.test(saved.id)) {
                    return {
                        id: saved.id,
                        started: saved.started === true
                    };
                }
            } catch  {
            /* A stale locator starts a new conversation. */ }
        }
        return {
            id: crypto.randomUUID(),
            started: false
        };
    });
    const [aiContext, setAiContext] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeChatId, setActiveChatId] = useState(restored.id);
    const [conversationStarted, setConversationStarted] = useState(restored.started);
    useEffect(()=>{
        if (storageKey) sessionStorage.setItem(storageKey, JSON.stringify({
            id: activeChatId,
            started: conversationStarted
        }));
    }, [
        storageKey,
        activeChatId,
        conversationStarted
    ]);
    const toggleLocal = useCallback(()=>setSidebarOpen((v)=>!v), []);
    // Record shell on (FND-20): one assistant. The pane's open state is the
    // layout store's assistOpen, so the topbar Assist button and ⌘J drive it, and
    // the page claims the assistant host so the shell's generic rail stays away.
    const shell = useProposalRecordShellEnabled();
    const storeOpen = useLayoutStore((s)=>s.assistOpen);
    const setStoreOpen = useLayoutStore((s)=>s.setAssistOpen);
    const toggleStore = useLayoutStore((s)=>s.toggleAssist);
    const setAssistHost = useLayoutStore((s)=>s.setAssistHost);
    useEffect(()=>{
        if (!shell) return;
        setAssistHost('page');
        return ()=>{
            setAssistHost('rail');
            setStoreOpen(false);
        };
    }, [
        shell,
        setAssistHost,
        setStoreOpen
    ]);
    // WRT-11: with writing_workspace_v1 on, a registered docked Assist tab is the
    // one assistant; the pane never opens, every open request goes to the tab.
    const writingWorkspace = useFeatureFlag('writing_workspace_v1') === true;
    const openDockedRef = useRef(null);
    const [dockedAssist, setDockedAssist] = useState(false);
    const host = useMemo(()=>({
            register: (openAssistTab)=>{
                openDockedRef.current = openAssistTab;
                setDockedAssist(true);
                return ()=>{
                    if (openDockedRef.current !== openAssistTab) return;
                    openDockedRef.current = null;
                    setDockedAssist(false);
                };
            }
        }), []);
    const docked = writingWorkspace && dockedAssist;
    useEffect(()=>{
        if (!docked || !shell || !storeOpen) return;
        openDockedRef.current?.();
        setStoreOpen(false);
    }, [
        docked,
        shell,
        storeOpen,
        setStoreOpen
    ]);
    const toggleBase = shell ? toggleStore : toggleLocal;
    const toggleSidebar = useCallback(()=>{
        if (docked && openDockedRef.current) openDockedRef.current();
        else toggleBase();
    }, [
        docked,
        toggleBase
    ]);
    const open = shell ? storeOpen : sidebarOpen;
    const setOpen = shell ? setStoreOpen : setSidebarOpen;
    const value = useMemo(()=>({
            aiContext,
            setAiContext,
            sidebarOpen: open,
            setSidebarOpen: setOpen,
            toggleSidebar,
            dockedAssist,
            activeChatId,
            setActiveChatId,
            conversationStarted,
            setConversationStarted
        }), [
        aiContext,
        open,
        setOpen,
        toggleSidebar,
        dockedAssist,
        activeChatId,
        conversationStarted
    ]);
    return /*#__PURE__*/ _jsx(OfficeWorkspaceAssistHostContext.Provider, {
        value: host,
        children: /*#__PURE__*/ _jsx(ProposalV2ReactContext.Provider, {
            value: value,
            children: children
        })
    });
}
export function useProposalV2Context() {
    const ctx = useContext(ProposalV2ReactContext);
    if (!ctx) {
        throw new Error('useProposalV2Context must be used inside <ProposalV2Provider>');
    }
    return ctx;
}
